import { useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useQuery } from "react-query";
import { toast } from "sonner";

import { Spinner } from "../../components";
import { DataTable } from "components/Table/DataTable";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { Input } from "@/components/ui/input";
import { ACTIVE_STYLES, StatusPill } from "components/Migrated/ui";
import { apiErrorMessage, moneefloApi, type Paged } from "service/moneeflo";

/** People who can sign in, with the role that decides what they may do. */

type UserRow = {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  status: string;
  hasPassword: boolean;
  lastLoginAt: string | null;
  organization: string | null;
  role: string | null;
};

const PER_PAGE = 25;

export default function StaffPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, error } = useQuery(
    ["moneeflo", "users", { search, page }],
    async () =>
      (
        await moneefloApi.get<Paged<UserRow>>("/users", {
          params: {
            search: search.trim() || undefined,
            page,
            perPage: PER_PAGE,
          },
        })
      ).data,
    { keepPreviousData: true },
  );

  if (isError) toast.error(apiErrorMessage(error));

  const columns = useMemo<ColumnDef<UserRow>[]>(
    () => [
      {
        accessorKey: "fullName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Name" />
        ),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.getValue("fullName")}</span>
            <span className="text-xs text-form-content-label">
              {row.original.email}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "role",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Role" />
        ),
        cell: ({ row }) => (
          <span className="capitalize">
            {String(row.getValue("role") ?? "-").replace(/_/g, " ")}
          </span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "organization",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Organization" />
        ),
        cell: ({ row }) => row.getValue("organization") ?? "-",
        enableSorting: false,
      },
      {
        accessorKey: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => (
          <StatusPill
            value={String(row.getValue("status"))}
            palette={ACTIVE_STYLES}
          />
        ),
        enableSorting: false,
      },
      {
        accessorKey: "hasPassword",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Can sign in" />
        ),
        // Passwords did not migrate from Xano: people set one via a reset link.
        cell: ({ row }) =>
          row.getValue("hasPassword") ? (
            "Yes"
          ) : (
            <span className="text-form-content-label">No password set yet</span>
          ),
        enableSorting: false,
      },
      {
        accessorKey: "lastLoginAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Last sign in" />
        ),
        cell: ({ row }) => {
          const value = row.original.lastLoginAt;
          return value ? new Date(value).toLocaleDateString("en-IN") : "-";
        },
        enableSorting: false,
      },
    ],
    [],
  );

  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));
  const withoutPassword = (data?.items ?? []).filter(
    (u) => !u.hasPassword,
  ).length;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-xl bg-white-A700">
      <div className="flex flex-wrap items-center gap-3 p-4">
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search by name or email"
          className="h-10 max-w-[320px] rounded-lg border-inputBorder-base"
        />
        <span className="ml-auto text-sm text-form-content-label">
          {total.toLocaleString("en-IN")} users
          {withoutPassword > 0 &&
            ` · ${withoutPassword} on this page cannot sign in yet`}
        </span>
      </div>

      <div className="relative flex-1 overflow-auto">
        {isLoading && <Spinner />}
        <DataTable
          data={data?.items ?? []}
          columns={columns}
          isPaginationVisible={false}
          isToolbarVisible={false}
        />
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-gray-border-gray-lite p-3 text-sm">
        <span className="text-form-content-label">
          Page {page} of {lastPage}
        </span>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="rounded-lg border border-gray-border-gray-lite px-3 py-1.5 disabled:opacity-40"
        >
          Previous
        </button>
        <button
          onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
          disabled={page >= lastPage}
          className="rounded-lg border border-gray-border-gray-lite px-3 py-1.5 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
